// Phase 2.46.13 — Default app-wide locale to India Standard Time (UTC+5:30).
//
// All AMDiris users are based in India. The backend persists all timestamps
// in UTC (correct), but the FRONTEND must always render them in IST,
// regardless of the user's browser timezone (some doctors travel; some
// machines have wrong system clocks).
//
// Phase 2.46.17 — also patch the Date CONSTRUCTOR so that ISO strings
// returned by the backend WITHOUT a timezone marker (e.g.
// "2026-06-06T18:24:48.161000") are interpreted as UTC, not local time.
// Many backend endpoints call `dt.isoformat()` on naive UTC datetimes,
// producing strings that JS would otherwise read as local time —
// resulting in audit-trail / system-health rows showing the wrong hour.
//
// We override `Date.prototype.toLocaleString`, `toLocaleDateString`,
// `toLocaleTimeString` so EVERY existing call site automatically renders
// in IST — no scattered edits across 100+ components. Explicit options
// (caller-provided `timeZone` / `locale`) are still respected.
//
// Import this file ONCE near the top of `src/index.js` (before <App/>).

const IST_TZ = "Asia/Kolkata";
const IN_LOCALE = "en-IN";

// Detect ISO-like strings without a timezone marker (no Z and no ±HH:MM).
// Example match:    2026-06-06T18:24:48
// Example match:    2026-06-06T18:24:48.161000
// Example no-match: 2026-06-06T18:24:48Z
// Example no-match: 2026-06-06T18:24:48+05:30
const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function maybeForceUTC(value) {
  if (typeof value === "string" && NAIVE_ISO_RE.test(value)) {
    return value + "Z";  // tell JS this naive ISO is UTC
  }
  return value;
}

// Patch the Date constructor so that bare naive-ISO strings from the
// backend are treated as UTC. All other Date(...) signatures pass through.
const NativeDate = Date;
function PatchedDate(...args) {
  if (args.length === 1) {
    args[0] = maybeForceUTC(args[0]);
  }
  // `new Date(...)` vs `Date(...)`:
  if (this instanceof PatchedDate) {
    return new NativeDate(...args);
  }
  return NativeDate(...args);
}
PatchedDate.prototype = NativeDate.prototype;
PatchedDate.now = NativeDate.now;
PatchedDate.parse = (s) => NativeDate.parse(maybeForceUTC(s));
PatchedDate.UTC = NativeDate.UTC;
// eslint-disable-next-line no-global-assign
Date = PatchedDate;

function patch(method) {
  const original = NativeDate.prototype[method];
  NativeDate.prototype[method] = function patched(locale, options) {
    // If the caller passed a locale arg that's already a non-default
    // value, honor it. Otherwise default to en-IN.
    const finalLocale = locale || IN_LOCALE;
    const finalOptions = {
      timeZone: IST_TZ,
      ...(options || {}),
    };
    // If the caller explicitly set a timeZone, respect it (don't override).
    if (options && options.timeZone) {
      finalOptions.timeZone = options.timeZone;
    }
    return original.call(this, finalLocale, finalOptions);
  };
}

patch("toLocaleString");
patch("toLocaleDateString");
patch("toLocaleTimeString");

// Convenience helper for code that wants an explicit IST formatter
// (e.g. when building strings for CSV exports or PDF generation).
export function fmtIST(date, options = {}) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(IN_LOCALE, {
    timeZone: IST_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    ...options,
  });
}
